import { Badge, Box, Button, Spinner } from "@chakra-ui/react";
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import {
  FiMoreVertical,
  FiPlus,
  FiRefreshCw,
  FiUserCheck,
  FiUserX,
} from "react-icons/fi";
import CreateUserModal from "./addUser";
import api from "../../../lib/api";
import { toaster } from "../../../components/ui/toaster";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  active: boolean;
  email_verified: boolean;
  created_at: string;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/auth/users");
      setUsers(response.data.data.users);
    } catch (error: any) {
      console.error("Fetch users error:", error);
      toaster.create({
        title: "Error",
        description: "Failed to fetch users",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId: number, currentStatus: boolean) => {
    try {
      const response = await api.put(`/auth/users/${userId}/status`, null, {
        params: { active: !currentStatus },
      });

      toaster.create({
        title: "Success",
        description:
          response.data.message ||
          `User ${!currentStatus ? "activated" : "deactivated"} successfully`,
        type: "success",
      });

      // Refresh users list
      fetchUsers();
    } catch (error: any) {
      console.error("Toggle status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to update user status";

      toaster.create({
        title: "Error",
        description: errorMessage,
        type: "error",
      });
    }
  };

  const handleUserCreated = () => {
    setIsModalOpen(false);
    fetchUsers();
  };

  return (
    <Box p={4} bg="white">
      <Box maxW="6xl" w={"100%"}>
        <Box
          display="flex"
          flexWrap={"wrap"}
          justifyContent="space-between"
          alignItems="center"
          mb={8}>
          <Box fontSize="xl" fontWeight="bold" color="gray.800">
            User Management
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outline"
              onClick={fetchUsers}
              borderRadius="lg"
              disabled={loading}>
              <FiRefreshCw />
              Refresh
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => setIsModalOpen(true)}
              borderRadius="lg">
              <FiPlus />
              Invite User
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box textAlign="center" py={12}>
            <Spinner size="lg" color="blue.500" />
          </Box>
        ) : (
          <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden">
            <Box p={3}>
              <Box fontSize="xl" fontWeight="semibold" color="gray.800" mb={6}>
                All Users
              </Box>

              <Box overflowX="auto">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead
                    style={{
                      backgroundColor: "#F9FAFB",
                      borderBottom: "1px solid #E5E7EB",
                    }}>
                    <tr>
                      <th
                        style={{
                          padding: "16px 24px",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}>
                        Name
                      </th>
                      <th
                        style={{
                          padding: "16px 24px",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}>
                        Email Address
                      </th>
                      <th
                        style={{
                          padding: "16px 24px",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}>
                        Role
                      </th>
                      <th
                        style={{
                          padding: "16px 24px",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}>
                        Status
                      </th>
                      <th
                        style={{
                          padding: "16px 24px",
                          textAlign: "left",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                        }}>
                        Verified
                      </th>
                      <th style={{ padding: "16px 24px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user.id}
                        style={{ borderBottom: "1px solid #E5E7EB" }}>
                        <td
                          style={{
                            padding: "16px 24px",
                            color: "#1F2937",
                            fontWeight: "500",
                          }}>
                          {user.first_name} {user.last_name}
                        </td>
                        <td style={{ padding: "16px 24px", color: "#6B7280" }}>
                          {user.email}
                        </td>
                        <td style={{ padding: "16px 24px", color: "#374151" }}>
                          {user.role}
                        </td>
                        <td style={{ padding: "16px 24px" }}>
                          <Badge
                            colorScheme={user.active ? "green" : "red"}
                            px={3}
                            py={1}
                            borderRadius="md">
                            {user.active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td style={{ padding: "16px 24px" }}>
                          <Badge
                            colorScheme={user.email_verified ? "blue" : "gray"}
                            px={3}
                            py={1}
                            borderRadius="md">
                            {user.email_verified ? "Verified" : "Pending"}
                          </Badge>
                        </td>
                        <td
                          style={{ padding: "16px 24px", textAlign: "center" }}>
                          <MenuRoot>
                            <MenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#6B7280",
                                }}>
                                <FiMoreVertical size={20} />
                              </Button>
                            </MenuTrigger>
                            <MenuContent>
                              <MenuItem
                                value={user.active ? "deactivate" : "activate"}
                                onClick={() =>
                                  handleToggleStatus(user.id, user.active)
                                }>
                                {user.active ? (
                                  <>
                                    <FiUserX />
                                    Deactivate User
                                  </>
                                ) : (
                                  <>
                                    <FiUserCheck />
                                    Activate User
                                  </>
                                )}
                              </MenuItem>
                            </MenuContent>
                          </MenuRoot>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          </Box>
        )}

        {isModalOpen && (
          <CreateUserModal
            onClose={() => setIsModalOpen(false)}
            onUserCreated={handleUserCreated}
          />
        )}
      </Box>
    </Box>
  );
}
